import React from "react";
import BaseComponent from "../../../form_components/BaseComponent";
import Utility from "../../../utilities/Utility";
import Switch from "../../../form_components/Switch";
import LastPulledWidget from './LastPulledWidget';
import Button from "../../../form_components/Button";
import BranchMergeEventFactory from "../../../utilities/versioning/BranchMergeEventFactory";
import { addBreakingChange, hasLocalChanges } from "../../../utilities";
import { BreakingChangeType } from "../../../types";
import { MergeDialog, MergeDialogType } from "../../dialogs/MergeDialog";
import { defaultServiceContext } from "../../../contexts";

const {
    avsService,
    errorMessageService,
    loadingService
} = defaultServiceContext;

export default class BranchingMergingUpgrade extends BaseComponent {
    /**
     * Global exception handling
     * @module BranchingMergingUpgrade
     * @param {Object} props React props
     */
    constructor(props) {
        super(props);

        this.state = {
            isBranchingMergingInitialized: !!props.serialized.branchingMerging?.initialized
        };
        this.branchMergeEventFactory = new BranchMergeEventFactory();
        this.getLatestApplication = this.props.getLatestApplication
    }

    componentDidMount(){
        this.updateUIState();
    }

    componentWillReceiveProps(){
        this.updateUIState();    
    }
    
    /**
     * Manages the `isBranchingMergingInitialized` key on branchingMerging
     * @param {string} key 
     * @param {boolean} value 
     */
    setConfig(key, value) {
        
        const serialized = window.apm.serialized;
        const shouldInitializeBranchingMerging = !serialized.branchingMerging?.initialized;

        if(shouldInitializeBranchingMerging && value === true){
            Utility.openApplicationOverlay("Checking for unsaved changes ...");
            this.openBranchAndMergeWindow('upgrade',serialized);
        }
    }

    getConfig(key) {
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing_operator
        // Dev note: branchingMerging doesn't have a 'key' property thus you can't use it in the optional chaining without returning the value
        // This can be done with brackets
        return window.apm.serialized.branchingMerging?.[key] ?? false;
    }

    /**
     * Control the UI (dropdowns) via state
     */
     updateUIState(){
        this.setState({isBranchingMergingInitialized: this.getConfig("initialized")});
    }

    /**
     * Modal to confirm Branching & Mergin upgrade.
     */
    confirmUpgrade(serialized) {
        w2confirm({
            msg: `<p>Upgrading to Branching & Merging requires an application save and is not backwards compatible. <b><u>This cannot be undone.</u></b> Do you wish to continue?</p>
                  <div style="display: flex; justify-content: center">
                    <input type="checkbox" onchange="document.getElementById('Yes').disabled = !document.getElementById('branchingMerging').checked" id="branchingMerging" name="branchingMerging" style="margin: 3px 4px;">
                    <label for="branchingMerging" style="font-weight: normal">Upgrade to Branching & Merging.</label>
                  </div>`,
            title: 'Upgrade to Branching & Merging',
            width: 453,
            height: 280,
            btn_yes: {
                text: 'Yes',
                style: '',
                class: 'primary',
                callBack: () =>  {
                    if(document.getElementById('branchingMerging').checked === true){
                        this.upgradeBranchingMerging(serialized);
                    } else {
                        document.getElementById('pleaseConfirm').style.display = 'block';
                    }
                }
                        
            },
            btn_no: {
                text: 'No',
                style: '',
                class: 'secondary',
                callBack: () => this.setState({isBranchingMergingInitialized: false})
            },
            callBack: null,     // common callBack
            onOpen: this.disableYes('#Yes').then((elm) => {
                elm.disabled = true;
            })
        });
    }

    disableYes(selector) {
        return new Promise(resolve => {
            if (document.querySelector(selector)) {
                return resolve(document.querySelector(selector));
            }
    
            const observer = new MutationObserver(mutations => {
                if (document.querySelector(selector)) {
                    resolve(document.querySelector(selector));
                    observer.disconnect();
                }
            });
    
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        });
    }

    upgradeBranchingMerging(serialized){
        let clonedSerialized = _.cloneDeep(serialized);

         // B&M requires that all data has id fields
        // B&M appProperties may not have ids
        clonedSerialized = this.checkAppPropertyIds(clonedSerialized, window.apm.timestamp);
        // B&M node and link model config items may not have ids
        clonedSerialized = this.checkModelConfigItemsIds(clonedSerialized, window.apm.timestamp);

        clonedSerialized.branchingMerging = {  
            initialized: true,
            lastLocalChange: window.apm.timestamp
        };

        addBreakingChange(clonedSerialized, {
            message: 'Branching and Merging enabled.',
            timestamp: window.apm.timestamp,
            type: BreakingChangeType.branchingEnabled
        });

        this.props.openSaveDialog(clonedSerialized);
    }

    async testForUnsavedChanges() {
        const { branchId } = window.apm.serialized;
        const latestVersion = (
            await avsService.getLatestVersion(branchId)
        )?.version;
        if (latestVersion === null) {
            return { neverSaved: true };
        }
        const latestDesign = await this.getLatestApplication(latestVersion);
        const serialized = window.apm.serialized;
        const unsavedChanges = hasLocalChanges({
            design: serialized,
            latestDesign
        });
        const notOnLatestVersion = serialized.version !== latestDesign.version;
        return {
            latestDesign,
            unsavedChanges,
            notOnLatestVersion
        };
    }

    openBranchAndMergeWindow(type, serialized = null) {
        const isBranchingMergingInitialized = this.state.isBranchingMergingInitialized;
        this.testForUnsavedChanges().then((results) => {
            
            // must already have been saved a first time
            if(results.neverSaved){
                window.apm.notification.post("error", "Please save to repository before upgrading");
                Utility.closeApplicationOverlay();
                this.setState({isBranchingMergingInitialized: false});
                return;
            } 
            // must be on latest version
            else if(results.notOnLatestVersion){
                window.apm.notification.post("error", "Merging or upgrading are only available from latest version");
                Utility.closeApplicationOverlay();
                if(!isBranchingMergingInitialized){
                    this.setState({isBranchingMergingInitialized: false});
                }
                return;
            } 
            // must have no unsaved changes
            else if(results.unsavedChanges){
                window.apm.notification.post("error", "Please save changes before merging or upgrading");
                if(!isBranchingMergingInitialized){
                    this.setState({isBranchingMergingInitialized: false});
                }
                Utility.closeApplicationOverlay();
                
                return;
            }
            // branch dialogs
            else {
                if(type === 'branch'){
                    window.apm.openCreateBranchWindow(type);
                }
                if(type === 'merge'){
                    window.apm.openMergeBranchWindow(results.latestDesign);  
                }
                if(type === 'upgrade'){
                    this.confirmUpgrade(serialized); 
                }

                Utility.closeApplicationOverlay();
            }    
            
        }).catch((error) => {
            window.apm.notification.post("error", "Error checking for unsaved changes");
            Utility.closeApplicationOverlay();
            this.setState({ isBranchingMergingInitialized: false });
            errorMessageService.addAvsUnresponsiveError(error);
        });
    }

    /**
     * Adds id fields to appProperties if they do not have it
     * @param {object} serialized 
     * @param {number} timestamp 
     * @returns serialized
     */
     checkAppPropertyIds (serialized, timestamp) {

        serialized.appProperties.forEach(appProp => {
            if(!appProp.id){
                appProp.id = Utility.uuid4();
                appProp.lastLocalChange = timestamp
            }
        });
    
        return serialized;
    }

    /**
     * Adds id fields to node/link model config items if they do not have it
     * @param {object} serialized 
     * @param {number} timestamp 
     * @returns serialized
     */
    checkModelConfigItemsIds(serialized, timestamp){
        serialized.components.modules.forEach(mod => {
            if(mod.localVariables && mod.localVariables.length > 0){
                this.updateLocalVariablePropsConfigIdsupdateVariableId(mod.localVariables, mod.props, timestamp);
            }
            
            if(mod.props.config.inputs && mod.props.config.inputs.length > 0){
                this.updateVariableId(mod.props.config.inputs, mod.props, timestamp);
            }
            if(mod.props.config.outputs && mod.props.config.outputs.length > 0){
                this.updateVariableId(mod.props.config.outputs, mod.props, timestamp);
            }
    
            // TODO: replace with usedIn in teh following component.objects forEach
            const nodes = mod.versions[1].flow.nodes;
            nodes.forEach(node => {
                if(node.props.config.inputs && node.props.config.inputs.length > 0){
                    this.updateVariableId(node.props.config.inputs, node.props, timestamp);
                }
                if(node.props.config.outputs && node.props.config.outputs.length > 0){
                    this.updateVariableId(node.props.config.outputs, node.props, timestamp);
                }
                if(node.props.config.function && node.props.config.function.arguments && node.props.config.function.arguments.length > 0){
                    this.updateVariableId(node.props.config.function.arguments, node.props, timestamp);
                }
                if(node.props.config.function && node.props.config.function.outputs && node.props.config.function.outputs.length > 0){
                    this.updateVariableId(node.props.config.function.outputs, node.props, timestamp);
                }
                // inputTimeOut (single item array)
                // matchOrder
                // outputTo
                
            })
    
            const links = mod.versions[1].flow.links;
            links.forEach(link => {
                if(link.props.config.inputs && link.props.config.inputs.length > 0){
                    this.updateVariableId(link.props.config.inputs, link.props, timestamp);
                }
                if(link.props.config.outputs && link.props.config.outputs.length > 0){
                    this.updateVariableId(link.props.config.outputs, link.props, timestamp);
                }
            })
        });
    
        // TODO: usedIn for Objects instead of nodes in modules 
        serialized.components.objects.forEach(obj => {
            if(obj.props.config.inputs && obj.props.config.inputs.length > 0){
                this.updateVariableId(obj.props.config.inputs, obj.props, timestamp);    
            }
            if(obj.props.config.outputs && obj.props.config.outputs.length > 0){
                this.updateVariableId(obj.props.config.outputs, obj.props, timestamp);    
            }
    
            if(obj.props.config.function && obj.props.config.function.arguments && obj.props.config.function.arguments.length > 0){
                this.updateVariableId(obj.props.config.function.arguments, obj.props, timestamp);    
            }
    
            if(obj.props.config.function && obj.props.config.function.outputs && obj.props.config.function.outputs.length > 0){
                this.updateVariableId(obj.props.config.function.outputs, obj.props, timestamp);    
            }
        });
    
        // TODO: variable.props.usedIn depricated, but cannot remove yet
        serialized.components.variables.forEach(variable => {
            // delete variable.props.usedIn;
        })
        
        serialized.globalIntents.forEach(gi => {
            gi.moduleProperties.props.config.inputs = gi.moduleProperties.props.config.inputs || [];
            gi.moduleProperties.props.config.inputs.forEach(input => {
                if(!input.id){
                    input.id = Utility.uuid4();
                    input.lastLocalChange = window.apm.timestamp;
                }
            });
    
            gi.moduleProperties.props.config.outputs = gi.moduleProperties.props.config.outputs || [];
            gi.moduleProperties.props.config.outputs.forEach(output => {
                if(!output.id){
                    output.id = Utility.uuid4();
                    input.lastLocalChange = window.apm.timestamp;
                }
            });
        })

        serialized.functions ??= [];
        serialized.functions.forEach(func => {
            if(func.parameters){
                func.parameters.forEach(param => {
                    if(!param.id){
                        param.id = Utility.uuid4();
                        func.lastLocalChange = timestamp;
                    }
                })
            }
        });
       
        return serialized;
    }
    
    updateVariableId(collection, props, timestamp){
        collection.forEach(variable => {
            if(!variable.id){
                variable.id = Utility.uuid4();
                props.lastLocalChange = timestamp;
            } 
        });
    
        return collection;
    }
    
    updateLocalVariablePropsConfigIdsupdateVariableId(collection, props, timestamp){
        collection.forEach(lv => {
            if(!lv.props.config.id){
                lv.props.config.id = Utility.uuid4();
                props.lastLocalChange = timestamp;
            } 
        });
    
        return collection;
    }

    render() {
        const branchingMergingInitialized = this.state.isBranchingMergingInitialized;
        const isBranch = this.props.serialized.branching;
        const isMain = branchingMergingInitialized && !isBranch;
        const hasBranch = this.props.serialized.hasOwnProperty('branchingMap');

        return (
            <div>
                {!branchingMergingInitialized &&
                    <div className="row">
                        <div className="col-md-6">
                            <Switch 
                                context={this} 
                                valKey={"isBranchingMerging"} 
                                label={"Branching & Merging"} 
                                checked={branchingMergingInitialized} 
                                tooltip="Upgrades application to use Branching &amp; Merging"
                                disabled={branchingMergingInitialized}
                            />
                            
                        </div>
                        <div className="col-md-6">
                            <div className="mergeWarning">
                                <span>Warning: Upgrade Cannot Be Undone</span>
                            </div>
                        </div>
                    </div>
                }

                {branchingMergingInitialized &&
                        <div>
                            { isMain &&   
                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                    <div>
                                        <Button 
                                            label="Create Branch" 
                                            callback={() => {
                                                Utility.openApplicationOverlay("Checking for unsaved changes ...");
                                                this.openBranchAndMergeWindow('branch')
                                            }}
                                            disabled={this.props.isSaveNewVersionChecked}
                                            />
                                    </div>
                                    {hasBranch &&
                                        <div>
                                            <Button
                                                label={"Merge Branch"}
                                                callback={() => {
                                                    Utility.openApplicationOverlay("Checking for unsaved changes ...");
                                                    this.openBranchAndMergeWindow('merge')
                                                }}
                                                disabled={this.props.isSaveNewVersionChecked}
                                                />
                                        </div>
                                    }
                                </div>
                            }

                            {isBranch &&    
                                <div style={{display: 'inline-block'}}>
                                    <Button 
                                        label={"Pull from Main"} 
                                        callback={async () => {
                                            const unsavedChangeLoadingId = loadingService.addMessage('Checking for unsaved changes ...');

                                            const {
                                                latestDesign,
                                                notOnLatestVersion,
                                                unsavedChanges
                                            } = await this.testForUnsavedChanges();
                                            loadingService.removeMessage(unsavedChangeLoadingId);
            
                                            if (notOnLatestVersion) {
                                                // must be on the latest version
                                                // TODO: replace alert 
                                                alert('Pulling is only available from latest version of this branch');
                                                return;
                                            } else if (unsavedChanges) {
                                                // must have no unsaved changes
                                                // TODO: replace alert 
                                                alert('Please save changes before pulling latest from main');
                                                return;
                                            }

                                            const pullLoadingId = loadingService.addMessage('Preparing pull for review...');
                                            const {
                                                sourceDesign,
                                                sourceOriginDesign,
                                                targetDesign,
                                                targetOriginDesign
                                            } = await avsService.getDocumentsForPull(latestDesign);
                                            loadingService.removeMessage(pullLoadingId);

                                            MergeDialog.open({
                                                defaultCommitMessage: `Branch ${sourceDesign.application} pulled at version ${sourceDesign.version}`,
                                                sourceDesign,
                                                sourceOriginDesign,
                                                targetDesign,
                                                targetOriginDesign,
                                                type: MergeDialogType.pull
                                            });
                                        }}
                                    />
                                    
                                    {/* Display how many version behind main, or 'already up to date' */}
                                    <div className="pull_status">
                                        <LastPulledWidget 
                                            serialized={this.props.serialized}
                                        />
                                    </div>
                                    
                                </div>
                            }

                        </div>
                }
            </div>
        );
    }
}